import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { TitleWithInfo, QuestionMark } from '../../../shared/fund/info-styles';
import propTypes from '../../common/prop-types';
import { formatValue } from '../shared-utils';
import useFundStateStore from '../../common/fund-state-hook';
import { recordStatisticsEvent } from '../../../../services/statistics-service';
import { SSeparator, Title, ExternalLink } from './styles';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px !important;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  & > *:nth-child(1) {
    order: -2;
  }

  & > *:nth-child(3) {
    order: ${props => (props.theme.isMobileOrTablet ? -1 : 0)};
  }

  & > div {
    min-width: ${props => (props.theme.isMobileOrTablet ? '100%' : '50%')};
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    > .title {
      font: ${props => props.theme.fontWeightBold} 14px/120% ${props => props.theme.fontFamilyBody};
      color: ${props => props.theme.secondaryColor};
      letter-spacing: 0.1px;
      text-transform: uppercase;
      margin-bottom: 4px;
      display: flex;
      justify-content: flex-start;
      height: 16px;

      .question-mark {
        ${QuestionMark}
      }
    }

    > .help {
      ${TitleWithInfo}
    }

    > .price {
      > * {
        font: ${props => props.theme.fontWeightRegular} 19px/120% ${props => props.theme.fontFamilyBody};
        color: ${props => props.theme.bodyColor};
        letter-spacing: 0.1px;
      }

      > .date {
        font-size: 14px;
        line-height: 16px;
        font-weight: ${props => props.theme.fontWeightRegular};
        margin-left: 8px;
      }
    }
  }
`;

const ExternalLinkContainer = styled.div`
  font-family: ${props => props.theme.fontFamilyBody};
  font-size: 16px;

  span.icon-lw-external-link {
    font-size: 12px;
    margin-right: 6px;
  }
`;

const Price = ({
  title,
  value,
  date,
  percentage,
  helpContent,
  handleClick,
}) => {
  let valueToShow;

  if (Array.isArray(value)) {
    const nonEmptyValues = _.compact(value);

    if (value.length === nonEmptyValues.length) {
      valueToShow = _.compact(value).map(v => formatValue(v, percentage)).join('/');
    }
  } else {
    valueToShow = formatValue(value, percentage);
  }

  if (!valueToShow) {
    return null;
  }

  return (
    <div>
      {helpContent.length !== 0 && (
        <div className="title help" onClick={handleClick(title, helpContent)} role="button" tabIndex={-1}>
          <span className="fee-title">{title}</span>
          <span className="question-mark">?</span>
        </div>
      )}
      {helpContent.length === 0 && (
        <div className="title">
          <span className="fee-title">{title}</span>
        </div>
      )}
      <div className="price">
        <span>{valueToShow}</span>
        {date && <span className="date">{`(${date})`}</span>}
      </div>
    </div>
  );
};

Price.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
  date: PropTypes.string,
  percentage: PropTypes.bool,
  helpContent: PropTypes.arrayOf(PropTypes.string),
  handleClick: PropTypes.func,
};

Price.defaultProps = {
  value: null,
  date: null,
  percentage: false,
  helpContent: [],
  handleClick: () => null,
};

export default function Pricing({ fund, isOnModal }) {
  const {
    id,
    fundType,
    buySpread,
    sellSpread,
    pdsFeesDate,
    dayEndEntryPrice,
    dayEndExitPrice,
    stockCode,
    dayEndNav,
    dayEndNavDate,
    dayEndMarketPrice,
    dayEndMarketPriceDate,
    dayEndPricingUpdateDate,
  } = fund;

  const set = useFundStateStore(state => state.set);

  const showTooltipModal = (title, helpContent) => event => {
    event.preventDefault();
    set(state => {
      state.showTooltip = true;
      state.tooltipTitle = title;
      state.tooltipContent = helpContent;
    });

    recordStatisticsEvent({
      event: 'fund_profile_tooltip_clicked',
      statisticable: 'fund',
      id,
      clicked_url: title.replace(/[\s,/]/g, '_'),
      page: window.location.pathname,
    });
  };

  const sharePriceClick = e => {
    e && e.preventDefault();
    const url = `https://www.marketindex.com.au/asx/${stockCode.replace(/asx:\s*/i, '').toLowerCase()}`;

    recordStatisticsEvent({
      event: 'fund_share_price_link_clicked',
      statisticable: 'fund',
      id,
      clicked_url: url,
      page: isOnModal ? `${window.location.pathname}#invest-modal` : `${window.location.pathname}#fund-fees`,
    });

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Using arrays enables content to be broken into paragraphs in the modal
  const buySellSpreadHelp = [
    'The buy/sell spread is charged whenever a transaction, is made such as making an investment into or withdrawal from a fund.',
    'The buy/sell spread is the difference between the entry (buy) and exit (sell) prices of the fund, and is charged as a percentage of the value of the trade being made.',
    'The buy/sell spread helps the fund cover transaction costs involved in the fund buying or selling assets, such as brokerage fees, bank fees, and government taxes.',
  ];

  const entryPriceHelp = [
    'This is the price charged per unit when buying units in the fund. ',
    'When you invest your money is moved into the fund and "units" are issued to you in return. The difference between the entry and exit price is the Buy Sell Spread.',
  ];

  const navPriceHelp = [
    'This is a calculation that indicates the fair value of a single share of the fund. It\'s a reference point to use when transacting units or shares in the fund.',
    'A fund’s NAV is the sum of all its assets (the value of its holdings in cash, shares, bonds, financial derivatives and other securities) less any liabilities, all divided by the number of shares or units outstanding.',
  ];

  const exitPriceHelp = [
    'This is the price charged per unit when selling units in the fund.',
    'When you withdraw your money "units" are sold and money is provided to you in return. The difference between the entry and exit price is the Buy Sell Spread.',
  ];

  const sharePriceHelp = [
    'This is the share price units in the fund were trading at on the exchange as recorded at the end of the last day of trading. To see the current live price, visit the exchange where the fund is traded or contact your broker.',
    'This price may be lower or higher than the Net Asset Value (NAV).',
  ];

  return (
    <Container>
      <Title>
        Pricing
        {!!stockCode && stockCode.toLowerCase().startsWith('asx:')
        && (
          <ExternalLinkContainer>
            <span className="icon-lw-external-link" />
            <span>Check&nbsp;</span>
            <ExternalLink onClick={sharePriceClick} role="button" tabIndex={0}>
              <span>{`${stockCode.replace(/asx:\s*/i, '').toUpperCase()} share price`}</span>
            </ExternalLink>
          </ExternalLinkContainer>
        )}
      </Title>
      <SSeparator />
      <PriceContainer>
        {fundType === 'Managed Fund' && !isOnModal && (
          <>
            <Price title="buy/sell spread" value={[buySpread, sellSpread]} date={pdsFeesDate} percentage helpContent={buySellSpreadHelp} handleClick={showTooltipModal} />
            <Price title="entry price" value={dayEndEntryPrice} date={dayEndPricingUpdateDate} helpContent={entryPriceHelp} handleClick={showTooltipModal} />
            <Price title="nav" value={dayEndNav} date={dayEndNavDate} helpContent={navPriceHelp} handleClick={showTooltipModal} />
            <Price title="exit price" value={dayEndExitPrice} date={dayEndPricingUpdateDate} helpContent={exitPriceHelp} handleClick={showTooltipModal} />
          </>
        )}
        {fundType === 'Managed Fund' && !!isOnModal && (
          <>
            <Price title="buy/sell spread" value={[buySpread, sellSpread]} date={pdsFeesDate} percentage />
            <Price title="entry price" value={dayEndEntryPrice} date={dayEndPricingUpdateDate} />
            <Price title="nav" value={dayEndNav} date={dayEndNavDate} />
            <Price title="exit price" value={dayEndExitPrice} date={dayEndPricingUpdateDate} />
          </>
        )}
        {fundType !== 'Managed Fund' && !isOnModal && (
          <>
            <Price title="nav" value={dayEndNav} date={dayEndNavDate} helpContent={navPriceHelp} handleClick={showTooltipModal} />
            <Price title="share price" value={dayEndMarketPrice} date={dayEndMarketPriceDate} helpContent={sharePriceHelp} handleClick={showTooltipModal} />
          </>
        )}
        {fundType !== 'Managed Fund' && !!isOnModal && (
          <>
            <Price title="nav" value={dayEndNav} date={dayEndNavDate} />
            <Price title="share price" value={dayEndMarketPrice} date={dayEndMarketPriceDate} />
          </>
        )}
      </PriceContainer>
    </Container>
  );
}

/* eslint-disable-next-line react/require-default-props */
Pricing.propTypes = {
  fund: propTypes.fund.isRequired,
  isOnModal: propTypes.bool,
};

Pricing.defaultProps = { isOnModal: false };
